import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

const comparisonData = [
  {
    feature: "Czas wyszukiwania",
    manual: "2-3 godziny na sesję",
    spreadsheet: "10-15 minut",
    winner: "spreadsheet",
  },
  {
    feature: "Liczba produktów",
    manual: "Ograniczona do wyników wyszukiwania",
    spreadsheet: "Tysiące w jednym miejscu",
    winner: "spreadsheet",
  },
  {
    feature: "Jakość wyników",
    manual: "Dużo niepasujących ofert",
    spreadsheet: "Tylko zweryfikowane produkty",
    winner: "spreadsheet",
  },
  {
    feature: "Oceny sprzedawców",
    manual: "Trzeba sprawdzać osobno",
    spreadsheet: "Zintegrowane w arkuszu",
    winner: "spreadsheet",
  },
  {
    feature: "Aktualizacje",
    manual: "Brak – za każdym razem od nowa",
    spreadsheet: "Regularne co kilka dni",
    winner: "spreadsheet",
  },
  {
    feature: "Porównywanie cen",
    manual: "Ręczne otwieranie wielu kart",
    spreadsheet: "Wszystko obok siebie",
    winner: "spreadsheet",
  },
  {
    feature: "Bezpieczeństwo",
    manual: "Brak dodatkowej weryfikacji",
    spreadsheet: "Wbudowane alerty bezpieczeństwa",
    winner: "spreadsheet",
  },
];

const methodsComparison = [
  {
    icon: "ri-search-line",
    title: "Ręczne wyszukiwanie",
    desc: "Tradycyjna metoda polegająca na samodzielnym przeszukiwaniu platformy. Wymaga dużo czasu i cierpliwości. Często prowadzi do przypadkowych znalezisk, ale też wielu nieudanych prób.",
    cons: ["Czasochłonne", "Wiele niepasujących wyników", "Brak systematyczności", "Łatwo przegapić okazje"],
  },
  {
    icon: "ri-file-excel-2-line",
    title: "Kakobuy Spreadsheet",
    desc: "Uporządkowany arkusz z tysiącami zweryfikowanych produktów. Oszczędza czas, zwiększa trafność wyszukiwania i pomaga podejmować świadome decyzje zakupowe.",
    pros: ["Błyskawiczne przeglądanie", "Zweryfikowane produkty", "Systematyczne kategorie", "Regularne aktualizacje"],
  },
];

const stats = [
  { value: "85%", label: "Oszczędności czasu" },
  { value: "10x", label: "Więcej produktów" },
  { value: "95%", label: "Trafności wyników" },
  { value: "24/7", label: "Dostępności" },
];

export default function PorownaniePage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://kakobuypolska.rest';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const pageUrl = `${siteUrl}/porownanie`;

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    url: pageUrl,
    name: 'Kakobuy Spreadsheet vs ręczne wyszukiwanie – porównanie',
    description: 'Sprawdź dlaczego Kakobuy Spreadsheet jest bezkonkurencyjny w porównaniu z ręcznym przeszukiwaniem platformy. Oszczędność czasu, lepsze wyniki i bezpieczeństwo.',
    inLanguage: 'pl',
    isPartOf: {
      '@type': 'WebSite',
      name: 'Kakobuy Polska Spreadsheet',
      url: siteUrl,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Strona główna', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Porównanie', item: pageUrl },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <main className="pt-16">
        <div className="relative w-full h-[350px] md:h-[420px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Editorial%20photography%20of%20two%20contrasting%20minimal%20workspace%20setups%20side%20by%20side%2C%20one%20desk%20cluttered%20with%20papers%20and%20sticky%20notes%20representing%20manual%20work%2C%20the%20other%20clean%20with%20a%20single%20laptop%20showing%20organized%20spreadsheet%2C%20warm%20cream%20and%20terracotta%20tones%2C%20soft%20natural%20lighting%2C%20modern%20editorial%20style%2C%20storytelling%20composition%20with%20depth%20and%20atmosphere&width=1800&height=900&seq=comparison-hero-01&orientation=landscape"
            alt="Porównanie Kakobuy Spreadsheet"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-background-50"></div>

          <div className="absolute bottom-0 left-0 right-0 z-10 pb-10">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="max-w-[700px]">
                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
                  Kakobuy Spreadsheet vs ręczne wyszukiwanie
                </h1>
                <p className="text-base md:text-lg text-white/90 leading-relaxed">
                  Sprawdź dlaczego Kakobuy Spreadsheet jest bezkonkurencyjny w porównaniu z ręcznym przeszukiwaniem platformy.
                </p>
              </div>
            </div>
          </div>
        </div>

        <section className="w-full py-10 md:py-16 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12 md:mb-16">
              {stats.map((stat, index) => (
                <div key={index} className="text-center p-6 bg-background-100 rounded-lg border border-background-200/70">
                  <div className="font-heading text-3xl md:text-4xl font-bold text-primary-500 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-600">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 md:mb-16">
              {methodsComparison.map((method, index) => (
                <div
                  key={index}
                  className="p-6 md:p-8 bg-background-100 rounded-lg border border-background-200/70"
                >
                  <div className="w-14 h-14 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 mb-5">
                    <i className={`${method.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-4">
                    {method.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-5">
                    {method.desc}
                  </p>
                  {method.cons && (
                    <div>
                      <span className="text-xs font-semibold text-accent-600 uppercase tracking-wide">Wady</span>
                      <ul className="mt-2 space-y-1.5">
                        {method.cons.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                            <i className="ri-close-circle-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {method.pros && (
                    <div>
                      <span className="text-xs font-semibold text-primary-600 uppercase tracking-wide">Zalety</span>
                      <ul className="mt-2 space-y-1.5">
                        {method.pros.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                            <i className="ri-checkbox-circle-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-16 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-10 text-center">
              Szczegółowe porównanie funkcji
            </h2>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse bg-background-50 rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-primary-500 text-background-50">
                    <th className="py-4 px-6 text-left text-sm font-semibold whitespace-nowrap">Funkcja</th>
                    <th className="py-4 px-6 text-left text-sm font-semibold whitespace-nowrap">Ręczne wyszukiwanie</th>
                    <th className="py-4 px-6 text-left text-sm font-semibold whitespace-nowrap">Kakobuy Spreadsheet</th>
                    <th className="py-4 px-6 text-center text-sm font-semibold whitespace-nowrap">Zwycięzca</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, index) => (
                    <tr
                      key={index}
                      className={`border-b border-background-200/70 ${
                        index % 2 === 0 ? 'bg-transparent' : 'bg-background-50'
                      }`}
                    >
                      <td className="py-4 px-6 text-sm font-medium text-foreground-900 whitespace-nowrap">
                        {row.feature}
                      </td>
                      <td className="py-4 px-6 text-sm text-foreground-600">
                        {row.manual}
                      </td>
                      <td className="py-4 px-6 text-sm text-foreground-600">
                        {row.spreadsheet}
                      </td>
                      <td className="py-4 px-6 text-center">
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
                          <i className="ri-trophy-line"></i>
                          Spreadsheet
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-16 bg-background-50">
          <div className="max-w-[800px] mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
              Przekonaj się sam – wypróbuj Kakobuy Spreadsheet
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-[560px] mx-auto mb-8">
              Dołącz do tysięcy zadowolonych użytkowników i zacznij robić szybsze, lepsze zakupy
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 rounded-md text-base font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Otwórz Kakobuy Spreadsheet
                <i className="ri-arrow-right-line"></i>
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-100 text-foreground-800 rounded-md text-base font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Strona główna
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}